// Mercapoli - Registro de Rider Público
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bike, Loader2, CheckCircle, MapPin, Phone, Mail, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import apiClient from '../lib/api';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const RegistroRider = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Paso 1: Datos personales
    nombre: '',
    email: '',
    telefono: '',
    documento_identidad: '',
    fecha_nacimiento: '',
    
    // Paso 2: Ubicación y vehículo
    ciudad: '',
    pais: 'GT',
    vehiculo: 'moto',
    licencia: '',
    placa_vehiculo: '',
    
    // Paso 3: Datos bancarios y términos
    banco: '',
    cuenta_banco: '',
    acepta_terminos: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.nombre || !formData.email || !formData.telefono || !formData.documento_identidad) {
        toast.error('Por favor completa todos los campos requeridos');
        return false;
      }
      // Validar email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        toast.error('Por favor ingresa un email válido');
        return false;
      }
    } else if (stepNum === 2) {
      if (!formData.ciudad || !formData.vehiculo) {
        toast.error('Por favor completa todos los campos requeridos');
        return false;
      }
    } else if (stepNum === 3) {
      if (!formData.acepta_terminos) {
        toast.error('Debes aceptar los términos y condiciones');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      await apiClient.post('/solicitudes/rider', formData);
      setSubmitted(true);
      toast.success('¡Solicitud enviada exitosamente!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">¡Solicitud Recibida!</h2>
            <p className="text-muted-foreground mb-6">
              Hemos recibido tu solicitud para ser Rider en Mercapoli. 
              Nuestro equipo la revisará y te contactaremos en las próximas 24-48 horas.
            </p>
            <div className="p-4 bg-brand-primary/10 rounded-lg mb-6">
              <p className="text-sm text-brand-primary font-medium">
                📱 Mientras tanto, descarga nuestra app para estar listo cuando seas aprobado.
              </p>
            </div>
            <Link to="/">
              <Button className="bg-brand-primary hover:bg-brand-secondary">
                Volver al inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Mercapoli" className="w-8 h-8" />
              <span className="font-heading font-bold">Mercapoli</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container-app py-8">
        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s === step ? 'bg-brand-accent text-black' : 
                  s < step ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {s < step ? <CheckCircle className="h-4 w-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 mx-1 ${s < step ? 'bg-green-500' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>

          <Card>
            <CardHeader className="text-center">
              <div className="w-14 h-14 rounded-xl bg-brand-accent/20 flex items-center justify-center mx-auto mb-4">
                <Bike className="h-7 w-7 text-brand-accent" />
              </div>
              <CardTitle>Quiero ser Rider</CardTitle>
              <CardDescription>
                {step === 1 && 'Cuéntanos sobre ti'}
                {step === 2 && 'Tu ubicación y vehículo'}
                {step === 3 && 'Datos bancarios y términos'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Step 1: Datos personales */}
              {step === 1 && (
                <>
                  <div className="space-y-2">
                    <Label>Nombre Completo *</Label>
                    <Input
                      value={formData.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Juan Pérez López"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>DPI / Cédula *</Label>
                    <Input
                      value={formData.documento_identidad}
                      onChange={(e) => handleChange('documento_identidad', e.target.value)}
                      placeholder="1234 56789 0101"
                    />
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="tu@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono *</Label>
                      <Input
                        value={formData.telefono}
                        onChange={(e) => handleChange('telefono', e.target.value)}
                        placeholder="+502 5555 6666"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Fecha de Nacimiento</Label>
                    <Input
                      type="date"
                      value={formData.fecha_nacimiento}
                      onChange={(e) => handleChange('fecha_nacimiento', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Debes ser mayor de 18 años</p>
                  </div>
                </>
              )}

              {/* Step 2: Ubicación y vehículo */}
              {step === 2 && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Ciudad *</Label>
                      <Input
                        value={formData.ciudad}
                        onChange={(e) => handleChange('ciudad', e.target.value)}
                        placeholder="Ciudad de Guatemala"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>País</Label>
                      <Select value={formData.pais} onValueChange={(v) => handleChange('pais', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GT">Guatemala</SelectItem>
                          <SelectItem value="MX">México</SelectItem>
                          <SelectItem value="CO">Colombia</SelectItem>
                          <SelectItem value="PE">Perú</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Tipo de Vehículo *</Label>
                    <Select value={formData.vehiculo} onValueChange={(v) => handleChange('vehiculo', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="moto">🏍️ Motocicleta</SelectItem>
                        <SelectItem value="bicicleta">🚲 Bicicleta</SelectItem>
                        <SelectItem value="auto">🚗 Automóvil</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.vehiculo !== 'bicicleta' && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Número de Licencia</Label>
                        <Input
                          value={formData.licencia}
                          onChange={(e) => handleChange('licencia', e.target.value)}
                          placeholder="A-123456"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Placa del Vehículo</Label>
                        <Input
                          value={formData.placa_vehiculo}
                          onChange={(e) => handleChange('placa_vehiculo', e.target.value)}
                          placeholder="P-123ABC"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="p-4 bg-brand-accent/10 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">💰 Ganancias estimadas</h4>
                    <p className="text-sm text-muted-foreground">
                      Como Rider en {formData.ciudad || 'tu ciudad'} puedes ganar entre <strong>Q3,000 - Q6,000</strong> mensuales 
                      trabajando a tiempo parcial.
                    </p>
                  </div>
                </>
              )}

              {/* Step 3: Datos bancarios y términos */}
              {step === 3 && (
                <>
                  <p className="text-sm text-muted-foreground">
                    Estos datos son necesarios para depositarte tus ganancias semanalmente.
                  </p>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Banco</Label>
                      <Select value={formData.banco} onValueChange={(v) => handleChange('banco', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona tu banco" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="banrural">Banrural</SelectItem>
                          <SelectItem value="bi">Banco Industrial</SelectItem>
                          <SelectItem value="bam">BAM</SelectItem>
                          <SelectItem value="bantrab">Bantrab</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Número de Cuenta</Label>
                      <Input
                        value={formData.cuenta_banco}
                        onChange={(e) => handleChange('cuenta_banco', e.target.value)}
                        placeholder="1234567890"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                    <h4 className="font-medium">Resumen de tu solicitud</h4>
                    <div className="grid gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Nombre:</span>
                        <span>{formData.nombre}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Ciudad:</span>
                        <span>{formData.ciudad}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Vehículo:</span>
                        <span className="capitalize">{formData.vehiculo}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="terms"
                      checked={formData.acepta_terminos}
                      onCheckedChange={(v) => handleChange('acepta_terminos', v)}
                    />
                    <label htmlFor="terms" className="text-sm text-muted-foreground">
                      Acepto los <a href="#" className="text-brand-primary underline">términos y condiciones</a>, 
                      la <a href="#" className="text-brand-primary underline">política de privacidad</a> y 
                      el <a href="#" className="text-brand-primary underline">código de conducta</a> de Riders Mercapoli.
                    </label>
                  </div>
                </>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between pt-4">
                {step > 1 ? (
                  <Button variant="outline" onClick={prevStep}>
                    Anterior
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 3 ? (
                  <Button onClick={nextStep} className="bg-brand-accent hover:bg-brand-accent/90 text-black">
                    Siguiente
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="bg-brand-accent hover:bg-brand-accent/90 text-black"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Enviar Solicitud
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Benefits */}
          <div className="mt-8 grid gap-4 sm:grid-cols-3 text-center">
            <div className="p-4">
              <div className="text-2xl mb-2">💰</div>
              <h4 className="font-medium text-sm">Gana bien</h4>
              <p className="text-xs text-muted-foreground">Q25+ por entrega</p>
            </div>
            <div className="p-4">
              <div className="text-2xl mb-2">⏰</div>
              <h4 className="font-medium text-sm">Horarios flexibles</h4>
              <p className="text-xs text-muted-foreground">Tú decides cuándo trabajar</p>
            </div>
            <div className="p-4">
              <div className="text-2xl mb-2">📲</div>
              <h4 className="font-medium text-sm">Pagos semanales</h4>
              <p className="text-xs text-muted-foreground">Depósito directo a tu cuenta</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistroRider;
